import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ReactComponent as LinkIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';
import { ReactComponent as BackIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-left.svg';
import { ReactComponent as CycleIcon } from '@repo/common/assets/icons/20px/icon_20px_undo-left.svg';
import { getOpsInboxItem, postOpsInboxAction } from '../../../api/twoDays/ops.api';
import Comments from '../../../components/TwoDays/Comments';
import { Select } from '../../../components/TwoDays/Modal';
import { JOB_LABEL, REJECT_REASONS, cn } from '../../../components/TwoDays/model/derive';
import useMedia, { MOBILE } from '../../../components/TwoDays/useMedia';
import { LogList, Pill, PillButton, RadioDot, SectionHead, TabPills, Tag } from '../../../components/TwoDays/ops/opsUi';
import { KST, formatClock, formatDue } from '../../../components/TwoDays/model/time';
import { WorkboardCtxOverride, useNow } from '../../../components/TwoDays/model/useWorkboard';
import { extOf, formatSize } from '../../../components/TwoDays/fileUtils';
import { OnePageView } from '../../../components/TwoDays/OnePageView';
import {
  deliverableLinkRows,
  stripScheme,
} from '../../../components/TwoDays/model/handoffSlots';
import TdButton from '../../../components/TwoDays/Button';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { ReactComponent as CrossIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import Linkify from '../../../components/TwoDays/Linkify';

/**
 * Lead inbox (Ops) — 받는 사람이 팀장인 제출 태스크를 운영진이 검토한다.
 * 위쪽 프로젝트 선택과 상관없이 모든 프로젝트의 모든 팀이 모이고, 팀으로 거른다.
 * 최종 결과물(마지막 단계)과 받는 사람 없는 리더 본인 태스크가 들어온다.
 * 검토 규칙은 워크보드의 받는 사람과 같다: 승인은 Task List 전부 ✓, 반려는 ✗ 항목 + 사유 + 설명.
 */

const card = 'rounded-2xl bg-grayscale-gray00 shadow-4';

const at = (iso) => `${formatDue(iso, KST)} KST`;
const reasonOf = (key) => {
  const i = REJECT_REASONS.findIndex((r) => r.key === key);
  return i < 0 ? null : `${i + 1} · ${REJECT_REASONS[i].label}`;
};

/** "Waiting 5h" — 제출 뒤 흐른 시간 */
const waitingLabel = (iso, now) => {
  const min = Math.max(0, Math.floor((now - new Date(iso)) / 60_000));
  return min < 60 ? `Waiting ${min}m` : `Waiting ${Math.floor(min / 60)}h`;
};

const kindLabel = (item) =>
  item.kind === 'final'
    ? 'Final deliverable'
    : item.kind === 'leader_own'
      ? 'Leader’s own task · no receiver'
      : 'No receiver';

/** 결과물 링크 — 여러 개(links), 예전 기록은 link 하나 */

/** 결과물 첨부 — 형식 · 용량 · 받기 */
const FileList = ({ files }) =>
  files?.length ? (
    <ul className='divide-y divide-green-deep03/[0.08] rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
      {files.map((f) => (
        <li key={f.name} className='flex items-center gap-3 px-4 py-3'>
          <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface-green10 text-[9px] font-medium uppercase text-green-deep04'>
            {extOf(f.name)}
          </span>
          <span className='flex min-w-0 flex-1 flex-col'>
            <span className='truncate text-[14px] text-text-primary'>{f.name}</span>
            <span className='text-[12px] text-text-secondary'>
              {f.kind} · {typeof f.size === 'number' ? formatSize(f.size) : f.size}
            </span>
          </span>
          {f.url && (
            <TdButton
              as='a'
              href={f.url}
              target='_blank'
              rel='noreferrer'
              variant='soft'
              size='s'
              className='shrink-0'
            >
              Download
            </TdButton>
          )}
        </li>
      ))}
    </ul>
  ) : null;

const sender = (item) => `${item.senderName}${item.senderLeader ? ' · Leader' : ''}`;

const isReviewed = (item) => item.state !== 'submitted';
const isRejected = (item) => item.state === 'rework_requested';

const CHIP_TONE = {
  submitted: 'bg-surface-green10 text-green-deep04',
  approved:
    'bg-functional-success01 text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]',
  rejected: 'bg-functional-failed01 text-functional-failed04 shadow-[inset_0_0_0_1px_#ff7474]',
  returned:
    'bg-functional-warning01 text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]',
  notScored: 'bg-grayscale-gray01 text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]',
};

const InboxChip = ({ tone, children }) => (
  <span
    className={cn(
      'inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-md px-[9px] text-[12px] font-medium',
      CHIP_TONE[tone],
    )}
  >
    {children}
  </span>
);

/** 상태 칩 — 최종 결과물은 승인 뒤 루브릭 채점 여부를 함께 */
const StatusChips = ({ item }) => (
  <span className='flex flex-wrap items-center gap-1.5'>
    {item.state === 'submitted' ? (
      <InboxChip tone='submitted'>Submitted</InboxChip>
    ) : isRejected(item) ? (
      item.review?.returned ? (
        <InboxChip tone='returned'>Returned</InboxChip>
      ) : (
        <InboxChip tone='rejected'>Rejected</InboxChip>
      )
    ) : (
      <>
        <InboxChip tone='approved'>Approved</InboxChip>
        {item.kind === 'final' && !item.rubric && (
          <InboxChip tone='notScored'>Not scored</InboxChip>
        )}
      </>
    )}
  </span>
);

const CodeChip = ({ code, large }) =>
  code ? (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-md bg-surface-green10 font-medium tabular-nums text-green-deep04',
        large ? 'h-7 px-2 text-[14px]' : 'h-[22px] px-[7px] text-[12px]',
      )}
    >
      {code}
    </span>
  ) : null;

/* ── 목록 ─────────────────────────────────────────────── */

const COLS =
  'grid grid-cols-[128px_minmax(0,1fr)_168px_48px_148px_104px_176px] items-center gap-4';

export const OpsLeadInbox = () => {
  const { inbox } = useOutletContext();
  const navigate = useNavigate();
  const now = useNow();
  const [tab, setTab] = useState('review');
  const [team, setTeam] = useState('all');

  const items = inbox ?? [];
  const toReview = items.filter((i) => !isReviewed(i));
  const reviewed = items.filter(isReviewed);
  // 팀 선택 — 프로젝트별로 묶는다
  const groups = [...new Set(items.map((i) => i.projectId))].map((pid) => ({
    id: pid,
    name: items.find((i) => i.projectId === pid).projectName,
    teams: [
      ...new Map(
        items.filter((i) => i.projectId === pid).map((i) => [i.teamId, i.teamName]),
      ).entries(),
    ],
  }));
  const manyProjects = groups.length > 1;
  const rows = (tab === 'review' ? toReview : reviewed)
    .filter((i) => team === 'all' || i.teamId === team)
    .sort((a, b) =>
      tab === 'review'
        ? new Date(a.submittedAt) - new Date(b.submittedAt) // 가장 오래 기다린 것부터
        : new Date(b.review?.at ?? 0) - new Date(a.review?.at ?? 0),
    );

  const tabBtn = (key, label, count) => {
    const active = tab === key;
    return (
      <button
        type='button'
        role='tab'
        aria-selected={active}
        onClick={() => setTab(key)}
        className={cn(
          '-mb-px flex h-11 items-center gap-2 border-b-2 text-[15px]',
          active
            ? 'border-green-deep02 font-medium text-text-primary'
            : 'border-transparent text-text-secondary',
        )}
      >
        {label}
        <span
          className={cn(
            'flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium',
            active
              ? 'bg-green-deep02 text-grayscale-gray00'
              : 'bg-grayscale-gray02 text-text-secondary',
          )}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-1.5'>
        <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
          Lead inbox
        </h1>
        <p className='text-[14px] text-text-secondary'>
          Final deliverables and leaders’ own tasks with no receiver.
        </p>
      </div>

      <div className='flex flex-wrap items-end justify-between gap-3 border-b border-green-deep03/[0.08]'>
        <div role='tablist' aria-label='Inbox' className='flex gap-5'>
          {tabBtn('review', 'To review', toReview.length)}
          {tabBtn('reviewed', 'Reviewed', reviewed.length)}
        </div>
        <div className='mb-2 flex items-center gap-3'>
          {tab === 'review' && (
            <span className='text-[13px] text-text-secondary'>Longest waiting first</span>
          )}
          <Select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            aria-label='Team'
            className='w-[180px]'
          >
            <option value='all'>All teams</option>
            {groups.map((g) => (
              <optgroup key={g.id} label={g.name}>
                {g.teams.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </div>
      </div>

      {inbox == null ? (
        <p className='py-10 text-center text-[14px] text-text-secondary'>Loading…</p>
      ) : rows.length === 0 ? (
        <div className={cn(card, 'px-6 py-14 text-center')}>
          <p className='text-[15px] font-medium text-text-primary'>
            {tab === 'review' ? 'Nothing to review' : 'Nothing reviewed yet'}
          </p>
          <p className='mt-1 text-[14px] text-text-secondary'>
            {tab === 'review'
              ? 'Final deliverables and leaders’ own tasks show up here when they’re submitted.'
              : 'Approved and rejected handoffs show up here.'}
          </p>
        </div>
      ) : (
        <div className='flex flex-col overflow-hidden rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00'>
          <div
            className={cn(
              COLS,
              'hidden h-10 border-b border-green-deep03/[0.08] px-5 text-[12px] font-medium text-text-secondary min-[1320px]:grid',
            )}
          >
            <span>Team</span>
            <span>Task</span>
            <span>Sender</span>
            <span>Ver.</span>
            <span>Submitted</span>
            <span>Waiting</span>
            <span>Status</span>
          </div>
          {rows.map((i) => (
            <button
              key={i.id}
              type='button'
              onClick={() => navigate(`/2days/ops/leads/${i.id}`)}
              className='w-full border-b border-green-deep03/[0.06] text-left text-text-primary last:border-b-0 hover:bg-green-deep03/[0.03]'
            >
              <span
                className={cn(
                  COLS,
                  'hidden min-h-[60px] px-5 py-2.5 text-[14px] leading-[140%] min-[1320px]:grid',
                )}
              >
                <span className='flex min-w-0 flex-col text-text-secondary'>
                  <span className='truncate'>{i.teamName}</span>
                  {manyProjects && (
                    <span className='truncate text-[12px] text-text-tertiary'>{i.projectName}</span>
                  )}
                </span>
                <span className='flex min-w-0 items-center gap-2'>
                  <CodeChip code={i.code} />
                  <span className='flex min-w-0 flex-col gap-0.5'>
                    <span className='truncate font-medium'>{i.title}</span>
                    {/* 첫 링크 (+N more) · 링크가 없으면 첫 파일 이름 */}
                    {i.preview && (
                      <span className='truncate text-[12px] leading-[140%] text-text-secondary'>
                        {i.preview}
                      </span>
                    )}
                  </span>
                </span>
                <span className='truncate'>{sender(i)}</span>
                <span className='tabular-nums text-text-secondary'>v{i.version}</span>
                <span className='tabular-nums text-text-secondary'>{at(i.submittedAt)}</span>
                <span className='font-medium tabular-nums text-functional-warning04'>
                  {isReviewed(i) ? '' : waitingLabel(i.submittedAt, now)}
                </span>
                <StatusChips item={i} />
              </span>
              <span className='flex flex-col gap-1.5 px-4 py-3.5 min-[1320px]:hidden'>
                <span className='flex min-w-0 items-center gap-2'>
                  <CodeChip code={i.code} />
                  <span className='min-w-0 flex-1 truncate text-[15px] font-medium leading-[140%]'>
                    {i.title}
                  </span>
                  {!isReviewed(i) && (
                    <span className='shrink-0 text-[13px] font-medium text-functional-warning04'>
                      {waitingLabel(i.submittedAt, now)}
                    </span>
                  )}
                </span>
                <span className='text-[13px] leading-[140%] text-text-secondary'>
                  {i.teamName} · {sender(i)} · v{i.version} · {at(i.submittedAt)}
                </span>
                <StatusChips item={i} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── 상세 ─────────────────────────────────────────────── */

/** 최종 결과물 루브릭 — 기준마다 1~3점 */
const RUBRIC = [
  {
    key: 'completion',
    label: 'Completion criteria',
    levels: [
      'One or more brief criteria missing or not verifiable',
      'All criteria met and verifiable in the deliverable',
      'All met, and the summary points to where each is verified',
    ],
  },
  {
    key: 'quality',
    label: 'Quality',
    levels: [
      'The core scenario (user input → rider screen) breaks or does not run',
      'One core scenario runs end to end, with at least one error state',
      'Plus no blockers from the chosen persona’s view, and cut scope is clear with reasons',
    ],
  },
  {
    key: 'change',
    label: 'Change handling',
    levels: [
      'The mid-project change notice is not reflected',
      'The change is reflected and noted in the summary',
      'Plus explains what was cut or reduced because of it, and why',
    ],
  },
];
const LEVEL = ['Below expectations', 'Meets expectations', 'Exceeds expectations'];

const Radio = ({ checked }) => (
  <span
    className={cn(
      'mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
      checked ? 'border-green-deep02' : 'border-grayscale-gray04',
    )}
  >
    {checked && <span className='h-2.5 w-2.5 rounded-full bg-green-deep02' />}
  </span>
);

const MarkButton = ({ kind, active, disabled, onClick }) => (
  <button
    type='button'
    aria-label={kind === 'pass' ? 'Mark ✓' : 'Mark ✗'}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex h-9 w-9 items-center justify-center rounded-lg border text-[14px] transition-colors disabled:cursor-default',
      active &&
        kind === 'pass' &&
        'border-functional-success03 bg-functional-success01 text-functional-success04',
      active &&
        kind === 'fail' &&
        'border-functional-failed03 bg-functional-failed01 text-functional-failed04',
      !active && 'border-green-deep03/[0.12] text-text-secondary enabled:hover:bg-grayscale-gray01',
    )}
  >
    {kind === 'pass' ? (
      <CheckIcon aria-hidden className='h-4 w-4' />
    ) : (
      <CrossIcon aria-hidden className='h-4 w-4' />
    )}
  </button>
);

/** 결정 패널 — 승인 · 반려(사유·설명) · 승인 뒤 되돌림 *//** 루브릭 — 최종 결과물이 승인된 뒤 3개 기준을 모두 골라야 저장된다. 저장하면 고른 것만 보인다 */
/* ── 상세 (시안 lead-inbox v2) — 머리 → Hero "your review" → 동료 평가 안내 · 루브릭 → Work → Activity / 정보 카드 ── */

const dtOf = (iso) => formatDue(iso, KST);
const INFO_COLS = 'grid-cols-[112px_minmax(0,1fr)]';

const InfoRow = ({ label, sub, children, first }) => (
  <div className={cn('grid gap-2 py-3 text-[14px] leading-[140%]', INFO_COLS, !first && 'border-t border-green-deep03/[0.08]')}>
    <span className='text-text-secondary'>{label}</span>
    <span className='flex min-w-0 flex-col gap-0.5 text-text-primary'>
      <span>{children}</span>
      {sub && <span className='text-[13px] text-text-secondary'>{sub}</span>}
    </span>
  </div>
);

/** 루브릭 — 최종 결과물 승인 뒤 3개 기준 (시안 showRubric) */
const Rubric = ({ item, act, busy }) => {
  const saved = item.rubric?.scores ?? null;
  const [scores, setScores] = useState({});
  const picked = saved ?? scores;
  const count = RUBRIC.filter((c) => picked[c.key]).length;
  return (
    <div className={cn(card, 'flex flex-col gap-4 px-6 py-5')}>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex flex-col gap-0.5'>
          <span className='text-[15px] font-medium leading-[140%] text-text-primary'>Rubric</span>
          <span className='text-[12px] text-text-secondary'>Last step · {count} of {RUBRIC.length} criteria</span>
        </div>
        {saved ? (
          <span className='inline-flex h-6 items-center rounded-md bg-functional-success01 px-[9px] text-[12px] font-medium text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]'>Scored {at(item.rubric.at)}</span>
        ) : (
          <Tag className='h-6'>Not scored</Tag>
        )}
      </div>
      {RUBRIC.map((c) => (
        <div key={c.key} className='flex flex-col gap-1.5'>
          <span className='text-[13px] font-medium leading-[140%] text-text-primary'>{c.label}</span>
          {c.levels.map((desc, i) => {
            const level = i + 1;
            const on = picked[c.key] === level;
            if (saved && !on) return null;
            return (
              <button key={level} type='button' role='radio' aria-checked={on} disabled={!!saved} onClick={() => setScores((s) => ({ ...s, [c.key]: level }))} className={cn('flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-left shadow-[inset_0_0_0_1px_rgba(0,158,79,0.1)]', !saved && 'hover:bg-green-deep03/[0.03]')}>
                <RadioDot on={on} />
                <span className='flex min-w-0 flex-col gap-0.5'>
                  <span className='text-[13px] font-medium leading-[140%] text-text-primary'>{level} · {LEVEL[i]}</span>
                  <span className='text-[13px] leading-[145%] text-text-secondary [text-wrap:pretty]'>{desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
      {!saved && (
        <div className='flex flex-col items-start gap-2'>
          <PillButton size='l' variant='primary' disabled={count < RUBRIC.length || busy} tip='Score all 3 criteria to save' onClick={() => act({ type: 'score', scores })}>
            Save rubric
          </PillButton>
          {count < RUBRIC.length && <span className='text-[13px] text-text-secondary'>Score all 3 criteria to save</span>}
        </div>
      )}
    </div>
  );
};

const RadioRow = ({ on, onClick, label, meta }) => (
  <button type='button' role='radio' aria-checked={on} onClick={onClick} className='flex min-h-10 items-start gap-2.5 rounded-[10px] px-2.5 py-1.5 text-left hover:bg-green-deep03/[0.05]'>
    <RadioDot on={on} />
    <span className='flex min-w-0 flex-col gap-0.5'>
      <span className='text-[14px] leading-[140%] text-text-primary'>{label}</span>
      {meta && <span className='text-[12px] text-text-secondary'>{meta}</span>}
    </span>
  </button>
);
const FormLabel = ({ children }) => <span className='text-[13px] font-medium leading-[140%] text-text-primary'>{children}</span>;
const formArea = 'w-full resize-y rounded-[10px] border border-green-deep03/20 bg-grayscale-gray00 px-3 py-2.5 text-[14px] leading-[150%] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none';

const ACT_TABS = [['all', 'All'], ['dec', 'Decisions'], ['cmt', 'Comments']];

export const OpsLeadInboxItem = () => {
  const { taskId } = useParams();
  const { reloadInbox, ackInbox } = useOutletContext();
  const now = useNow();
  const mobile = useMedia(MOBILE);
  const desk = useMedia('(min-width: 1180px)');
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [marks, setMarks] = useState({});
  const [opened, setOpened] = useState(null); // 열어 본 시각
  const [viewV, setViewV] = useState(null); // 보는 버전 (null = 지금)
  const [mode, setMode] = useState(null); // reject · return
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [cq, setCq] = useState(null); // 되돌림에 잇는 질문 댓글
  const [tab, setTab] = useState('all');
  const [cType, setCType] = useState('question');
  const [cTarget, setCTarget] = useState('');
  const [cBody, setCBody] = useState('');

  useEffect(() => {
    setDetail(null);
    setError('');
    setViewV(null);
    setMode(null);
    setOpened(null);
    getOpsInboxItem(taskId)
      .then((d) => {
        setDetail(d);
        ackInbox(`${d.item.id}:v${d.item.version}`); // 열어 봤으면 토스트·알림음 끝
      })
      .catch(() => setError('Couldn’t load this item.'));
  }, [taskId, ackInbox]);

  const item = detail?.item;
  const slotRows = deliverableLinkRows(detail?.deliverable ?? null);
  const slotLabels = (detail?.deliverable?.slots ?? []).filter((s) => s.url).map((s) => s.l);
  const taskList = [...slotLabels, ...(detail?.taskList ?? [])];

  // 검토가 끝났으면 표시는 결과로 정한다 — 승인은 전부 ✓, 반려는 ✗ 항목 외 ✓
  useEffect(() => {
    if (!item) return;
    if (item.state === 'approved') setMarks(Object.fromEntries(taskList.map((t) => [t, 'pass'])));
    else if (isRejected(item)) {
      const failed = new Set(item.review?.failedItems ?? []);
      setMarks(Object.fromEntries(taskList.map((t) => [t, failed.has(t) ? 'fail' : 'pass'])));
    } else setMarks({});
    setMode(null);
    setReason('');
    setText('');
    setCq(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.state, item?.version]);

  const act = async (action) => {
    setBusy(true);
    setError('');
    try {
      setDetail(await postOpsInboxAction(taskId, action));
      reloadInbox();
      return true;
    } catch (e) {
      setError(e.response?.data?.msg || 'Couldn’t save. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // 댓글 스레드는 워크보드 컴포넌트를 그대로 — 팀장으로 쓴다
  const commentCtx = useMemo(() => {
    if (!detail) return null;
    const members = detail.members ?? [];
    return {
      data: { viewerId: 'npc', members, comments: detail.comments ?? [] },
      memberById: new Map(members.map((m) => [m.id, m])),
      now,
      displayTz: KST,
      readOnly: false,
      dispatch: (a) => {
        if (a.type !== 'addComment') return;
        const c = a.comment;
        act({ type: 'comment', comment: { type: c.type, targetUserId: c.targetUserId, body: c.body, parentId: c.parentId } });
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, now]);

  const back = (
    <Link to='/2days/ops/leads' className='-ml-1.5 flex h-8 w-fit items-center gap-1.5 rounded-lg pl-1.5 pr-2.5 text-[14px] font-medium text-text-secondary hover:bg-green-deep03/[0.06] hover:text-text-primary'>
      <BackIcon className='h-4 w-4' />
      Lead inbox
    </Link>
  );

  if (!detail)
    return (
      <div className='flex flex-col gap-6'>
        {back}
        <p className='py-10 text-center text-[14px] text-text-secondary'>{error || 'Loading…'}</p>
      </div>
    );

  const sub = item.state === 'submitted';
  const approvedS = item.state === 'approved';
  const final = item.kind === 'final';
  const members = detail.members ?? [];
  const memberName = (id) => (id === 'npc' ? 'Team lead Kim' : (members.find((m) => m.id === id)?.name ?? 'Someone'));
  const history = Array.isArray(detail.history) ? detail.history : [];
  const chain = [...history.map((h) => ({ version: h.version, deliverable: h.deliverable, review: h.review })), { version: item.version, deliverable: detail.deliverable, review: item.review }];
  const viewing = chain.find((v) => v.version === (viewV ?? item.version)) ?? chain[chain.length - 1];
  const prev = history[history.length - 1];
  const lastRejection = prev?.review?.result === 'rejected' && !prev.review.returned ? prev.review : null;
  const yN = Object.values(marks).filter((v) => v === 'pass').length;
  const nN = Object.values(marks).filter((v) => v === 'fail').length;
  const failed = Object.entries(marks).filter(([, v]) => v === 'fail').map(([k]) => k);
  const keys = taskList.length;
  const allPass = keys > 0 && yN === keys;
  const rubricSaved = !!item.rubric?.scores;
  const showRubric = final && approvedS;
  const ended = item.projectEndAt && now >= new Date(item.projectEndAt);
  const toggle = (t, kind) =>
    setMarks((m) => {
      const next = { ...m };
      if (next[t] === kind) delete next[t];
      else next[t] = kind;
      return next;
    });
  const openLink = () => {
    if (opened == null) setOpened(now);
  };
  const waitT = sub ? waitingLabel(item.submittedAt, now) : '';
  const decided = approvedS ? `Approved ${at(item.review?.at)}` : item.review?.returned ? `Returned ${at(item.review?.at)}` : item.state === 'rework_requested' ? `Rejected ${at(item.review?.at)}` : '';
  const rejInfo = item.state === 'rework_requested' && !item.review?.returned ? [reasonOf(item.review?.reason), item.review?.description].filter(Boolean) : null;
  const retInfo = item.review?.returned ? [item.review?.description].filter(Boolean) : null;

  // ── 칩 · 정보 카드 · Hero (시안 d.chips · d.info · d.na)
  const chips = [
    sub ? { label: 'In review', tone: 'accent' } : approvedS ? { label: 'Approved', tone: 'success' } : item.review?.returned ? { label: 'Returned', tone: 'warning' } : { label: 'Rejected', tone: 'failed' },
    ...(lastRejection ? [{ label: 'Resubmission', tone: 'neutral' }] : []),
    ...(showRubric && !rubricSaved ? [{ label: 'Rubric to score', tone: 'warning' }] : []),
    ...(rubricSaved ? [{ label: 'Rubric scored', tone: 'success' }] : []),
  ];
  const naKind = sub ? 'review' : showRubric && !rubricSaved ? 'rubric' : approvedS ? 'approved' : item.review?.returned ? 'returned' : 'rejected';
  const NA = {
    review: ['border-green-deep02 bg-surface-green10', 'bg-green-deep02', `Review v${item.version} and decide`, `Open the deliverable, mark each handoff link and Task List item ✓ or ✗, then approve or reject. ${yN} ✓ · ${nN} ✗ · ${keys - yN - nN} left.`],
    rubric: ['border-functional-warning03 bg-functional-warning01', 'bg-functional-warning03', 'Score the rubric', `Approved ${at(item.review?.at)}. Score all 3 criteria to finish the final review.`],
    approved: ['border-green-deep03/[0.12] bg-grayscale-gray00', 'bg-functional-success03', decided, rubricSaved ? `Rubric scored ${at(item.rubric.at)}.` : 'No further action.'],
    rejected: ['border-green-deep03/[0.12] bg-grayscale-gray00', 'bg-functional-failed03', `${decided} · waiting for v${item.version + 1}`, rejInfo ? rejInfo.join(' — ') : ''],
    returned: ['border-green-deep03/[0.12] bg-grayscale-gray00', 'bg-functional-warning03', decided, retInfo ? retInfo.join(' — ') : ''],
  }[naKind];
  const steps = [
    ['Open deliverable', opened != null || !sub],
    ['Check links and Task List', yN + nN === keys || !sub],
    ['Approve or reject', !sub],
    ...(final ? [['Score rubric', rubricSaved]] : []),
  ];
  const curStep = steps.findIndex(([, done]) => !done);
  const rejectHint = nN === 0 ? 'Mark at least one link or item ✗' : !reason ? 'Pick a reason' : !text.trim() ? 'Add a description' : '';
  const questions = (detail.comments ?? []).filter((c) => c.type === 'question' && !c.parentId && c.authorId === 'npc');
  const returnHint = questions.length === 0 ? 'Post a question comment first' : cq == null ? 'Link a question comment' : !reason ? 'Pick a reason' : !text.trim() ? 'Add a description' : '';
  const info = [
    ['Team', item.teamName, ''],
    ['Sender', sender(item), 'Receiver · Team lead Kim'],
    ['Version', `v${item.version} of ${chain.length}`, lastRejection ? `Previous rejection: ${reasonOf(lastRejection.reason) ?? lastRejection.description}` : ''],
    ['Submitted', at(item.submittedAt), sub ? waitT : decided],
    ...(final && item.projectEndAt ? [['Project end', at(item.projectEndAt), 'Final review closes the project']] : []),
  ];

  // ── Activity — 전체 버전 기록 (시안 ev)
  const ev = [];
  const E = (t, who, title, o = {}) => ev.push({ n: `${o.kind}-${title}-${t}`, at: t, who, title, time: dtOf(t), ...o });
  chain.forEach((x) => {
    const d = x.deliverable ?? {};
    E(d.submittedAt, sender(item), `Submitted v${x.version}`, { key: true, kind: 'dec', detail: d.summary ?? '' });
    if (final && x.version === 1 && d.submittedAt) E(d.submittedAt, 'System', 'Peer review opened · 3 working hours per member', { kind: 'sys' });
    const r = x.review;
    if (r?.at) {
      const title = r.result === 'approved' ? `Approved v${x.version}` : r.returned ? `Returned v${x.version}` : `Rejected v${x.version}`;
      E(r.at, 'Team lead Kim', title, { key: true, kind: 'dec', ops: true, detail: [reasonOf(r.reason), r.description].filter(Boolean).join(' — ') });
    }
  });
  if (item.rubric?.at) E(item.rubric.at, 'Team lead Kim', 'Scored the rubric', { key: true, kind: 'dec', ops: true, detail: RUBRIC.map((c) => `${c.label} ${item.rubric.scores?.[c.key] ?? '–'}`).join(' · ') });
  if (opened != null) E(opened.toISOString(), 'Team lead Kim', `Opened v${item.version}`, { kind: 'dec', ops: true });
  (detail.comments ?? []).forEach((c) => {
    if (['rejection', 'return'].includes(c.type)) return;
    const mine = c.authorId === 'npc';
    E(c.createdAt, memberName(c.authorId), c.parentId ? 'Replied' : c.type === 'question' ? `Asked ${memberName(c.targetUserId)} a question` : 'Commented', { key: !c.parentId, kind: 'cmt', ops: mine, detail: c.body ? `“${c.body}”` : '' });
  });
  ev.sort((a, b) => new Date(b.at ?? 0) - new Date(a.at ?? 0));
  const F = { all: ev, dec: ev.filter((e) => e.kind === 'dec'), cmt: ev.filter((e) => e.kind === 'cmt') };
  const actTabs = ACT_TABS.map(([k, l]) => [k, `${l} · ${F[k].length}`]);
  const canPost = !!cBody.trim() && (cType === 'general' || !!cTarget);
  const post = async () => {
    const ok = await act({ type: 'comment', comment: { type: cType, targetUserId: cType === 'question' ? cTarget : null, body: cBody.trim(), parentId: null } });
    if (ok) {
      setCBody('');
      setCTarget('');
    }
  };

  const infoCard = (
    <aside className={cn(card, 'flex min-w-0 flex-col px-4 py-1')}>
      {info.map(([k, v, s], i) => (
        <InfoRow key={k} first={i === 0} label={k} sub={s || null}>
          {v}
        </InfoRow>
      ))}
    </aside>
  );

  const deliverable = (
    <div className={cn(card, 'flex flex-col gap-3.5 px-6 py-5')}>
      <span className='flex items-center justify-between gap-3 text-[15px] font-medium leading-[140%] text-text-primary'>
        Deliverable{viewing.version !== item.version ? ` · v${viewing.version}` : ''}
        {viewing.version !== item.version && (
          <button type='button' onClick={() => setViewV(null)} className='text-[13px] font-medium text-green-deep03 underline underline-offset-2'>
            Back to v{item.version}
          </button>
        )}
      </span>
      {deliverableLinkRows(viewing.deliverable).length > 0 && (
        <div className='flex flex-wrap items-center gap-3 rounded-xl bg-green-deep03/[0.03] px-3.5 py-3 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.08)]'>
          <LinkIcon className='h-5 w-5 shrink-0 text-icon-secondary' />
          <div className='flex min-w-[180px] flex-1 flex-col gap-0.5'>
            {deliverableLinkRows(viewing.deliverable).map((r) => (
              <a key={r.url} href={r.url} target='_blank' rel='noreferrer' onClick={openLink} className='truncate text-[14px] leading-[140%] text-text-primary hover:underline'>
                <span className='font-medium'>{r.label ?? 'Link'}</span>
                <span className='text-text-secondary'> · {stripScheme(r.url)}</span>
              </a>
            ))}
            {viewing.version === item.version && (
              <span className={cn('text-[12px] leading-[140%]', opened != null ? 'text-functional-success04' : 'text-text-secondary')}>{opened != null ? `Opened by you at ${formatClock(opened, KST)} KST` : 'Not opened yet'}</span>
            )}
          </div>
          <PillButton size='s' className='h-9 px-4 text-[14px]' onClick={() => { openLink(); window.open(deliverableLinkRows(viewing.deliverable)[0].url, '_blank', 'noreferrer'); }}>
            Open link
          </PillButton>
        </div>
      )}
      <FileList files={viewing.deliverable?.files} />
      <div className='flex flex-col gap-1'>
        <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>Summary</span>
        <p className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary [text-wrap:pretty]'>
          <Linkify text={viewing.deliverable?.summary} />
        </p>
      </div>
      {viewing.deliverable?.onePage?.length > 0 && <OnePageView items={viewing.deliverable.onePage} />}
      {prev && viewing.version === item.version && (
        <p className='flex items-center gap-2 text-[14px]'>
          <span className='text-text-secondary'>Previous version</span>
          <button type='button' onClick={() => setViewV(prev.version)} className='font-medium text-green-deep03 underline underline-offset-2'>
            v{prev.version} · {prev.review?.returned ? 'Returned' : prev.review?.result === 'approved' ? 'Approved' : 'Rejected'}
          </button>
        </p>
      )}
    </div>
  );

  const groups = [
    ...(slotLabels.length ? [['Handoff links', slotLabels.map((l, i) => [l, slotRows[i] ? `${l} · ${stripScheme(slotRows[i].url)}` : l])]] : []),
    [final ? 'Task List' : 'Task List', (detail.taskList ?? []).map((l) => [l, l])],
  ];

  return (
    <div className={cn('grid items-start', desk ? 'grid-cols-[minmax(0,1fr)_320px] gap-x-10 gap-y-6' : 'grid-cols-1', mobile ? 'gap-4' : 'gap-5')}>
      <header className='flex min-w-0 flex-col gap-3'>
        {back}
        <span className='text-[13px] leading-[140%] text-text-secondary'>
          {item.teamName} · {final ? 'Final deliverable' : 'Leader’s own task'}
        </span>
        <h1 className='m-0 flex flex-wrap items-center gap-2.5 text-[24px] font-medium leading-[130%] tracking-[-0.01em] text-text-primary'>
          <CodeChip code={item.code} large />
          {item.title}
        </h1>
        <div className='flex flex-wrap gap-1.5'>
          {chips.map((c) => (
            <Pill key={c.label} tone={c.tone}>{c.label}</Pill>
          ))}
        </div>
      </header>

      {desk && <div className='row-span-2'>{infoCard}</div>}

      <div className={cn('flex min-w-0 flex-col', mobile ? 'gap-7' : 'gap-9')}>
        {error && <p className='text-[14px] text-functional-failed04'>{error}</p>}

        {/* Hero — Lead inbox · your review */}
        <section aria-label='Your review' className={cn('flex flex-col gap-[18px] rounded-2xl border-[1.6px] shadow-4', mobile ? 'p-4' : 'px-6 py-5', NA[0])}>
          <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
            <span className='flex items-center gap-2'>
              <span className='inline-flex h-[22px] items-center rounded-full bg-green-deep04 px-[9px] text-[12px] font-medium text-grayscale-gray00'>Ops</span>
              <span className='text-[13px] font-medium leading-[140%] text-text-secondary'>Lead inbox · your review</span>
            </span>
            <span className='text-[13px] leading-[140%] tabular-nums text-text-secondary'>{sub ? `${waitT} · since ${formatClock(new Date(item.submittedAt), KST)} KST` : decided}</span>
          </div>
          <div className='flex items-start gap-3'>
            <span className={cn('mt-2 h-2.5 w-2.5 shrink-0 rounded-full', NA[1])} />
            <div className='flex min-w-0 flex-col gap-1'>
              <span className={cn('font-medium leading-[130%] text-text-primary [text-wrap:pretty]', mobile ? 'text-[18px]' : 'text-[20px]')}>{NA[2]}</span>
              {NA[3] && <span className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>{NA[3]}</span>}
            </div>
          </div>
          <ol aria-label='Review steps' className={cn('grid gap-2', mobile ? 'grid-cols-2' : steps.length === 4 ? 'grid-cols-4' : 'grid-cols-3')}>
            {steps.map(([label, done], i) => {
              const cur = i === curStep && (sub || approvedS);
              return (
                <li key={label} className={cn('flex min-h-10 min-w-0 items-center gap-2 rounded-xl px-3 py-2', cur ? 'bg-surface-green10 shadow-[inset_0_0_0_1.6px_#1dc37b]' : 'shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]')}>
                  <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium', done ? 'bg-green-deep02 text-grayscale-gray00' : cur ? 'bg-green-deep04 text-grayscale-gray00' : 'bg-green-deep03/[0.08] text-text-secondary')}>{done ? '✓' : i + 1}</span>
                  <span className={cn('min-w-0 text-[13px] font-medium leading-[130%]', cur ? 'text-text-primary' : done ? 'text-text-secondary' : 'text-text-tertiary')}>{label}</span>
                </li>
              );
            })}
          </ol>
          <div className='flex flex-col gap-4'>
            {sub && opened == null && slotRows[0] && (
              <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
                <PillButton size='l' variant='primary' onClick={() => { openLink(); window.open(slotRows[0].url, '_blank', 'noreferrer'); }}>
                  Open deliverable
                </PillButton>
                <span className='text-[13px] leading-[140%] text-text-secondary'>Opening is recorded in the log.</span>
              </div>
            )}
            {sub && mode !== 'reject' && (
              <div className='flex flex-col items-start gap-2'>
                <PillButton size='l' variant='primary' disabled={!allPass || busy} tip='Mark every link and Task List item ✓ to approve' onClick={() => act({ type: 'approve' })}>
                  Approve
                </PillButton>
                {!allPass && <span className='text-[13px] leading-[140%] text-text-secondary'>Mark every link and Task List item ✓ to approve</span>}
                <PillButton size='l' className='text-functional-failed04 hover:bg-functional-failed01' onClick={() => { setMode('reject'); setReason(failed.some((f) => slotLabels.includes(f)) ? REJECT_REASONS[0].key : ''); setText(''); }}>
                  Reject
                </PillButton>
              </div>
            )}
            {sub && mode === 'reject' && (
              <div className='flex flex-col gap-3.5'>
                <span className={cn('text-[13px] font-medium', nN ? 'text-functional-failed04' : 'text-text-secondary')}>{nN} item{nN === 1 ? '' : 's'} marked ✗</span>
                <div className='flex flex-col gap-1'>
                  <FormLabel>Reason</FormLabel>
                  {REJECT_REASONS.map((r) => (
                    <RadioRow key={r.key} on={reason === r.key} onClick={() => setReason(r.key)} label={r.label} />
                  ))}
                </div>
                <label className='flex flex-col gap-1.5'>
                  <FormLabel>Description</FormLabel>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder='What needs to change?' rows={3} className={formArea} />
                </label>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='flex-1 text-[13px] text-text-secondary'>{rejectHint}</span>
                  <PillButton onClick={() => setMode(null)}>Cancel</PillButton>
                  <PillButton variant='dangerFill' disabled={!!rejectHint || busy} tip={rejectHint} onClick={() => act({ type: 'reject', failedItems: failed, reason, description: text.trim() })}>
                    Reject
                  </PillButton>
                </div>
              </div>
            )}
            {approvedS && mode !== 'return' && (
              <div className='flex flex-col items-start gap-2 border-t border-green-deep03/[0.08] pt-3.5'>
                <PillButton disabled={!!ended} tip='Return is only available while the project is in progress.' onClick={() => { setMode('return'); setReason(''); setText(''); setCq(null); }}>
                  Return
                </PillButton>
                <span className='text-[13px] leading-[140%] text-text-secondary'>{ended ? 'Return is only available while the project is in progress.' : 'Send back after approval, with a linked question.'}</span>
              </div>
            )}
            {approvedS && mode === 'return' && (
              <div className='flex flex-col gap-3.5 border-t border-green-deep03/[0.08] pt-3.5'>
                <span className='text-[14px] font-medium text-text-primary'>Return</span>
                <div className='flex flex-col gap-1'>
                  <FormLabel>Linked question</FormLabel>
                  {questions.length === 0 ? (
                    <span className='rounded-[10px] bg-green-deep03/[0.04] px-3 py-2.5 text-[13px] leading-[150%] text-text-secondary'>No question comments yet. Post one in Comments first.</span>
                  ) : (
                    questions.map((q) => <RadioRow key={q.id} on={cq === q.id} onClick={() => setCq(q.id)} label={q.body} meta={`To ${memberName(q.targetUserId)} · ${at(q.createdAt)}`} />)
                  )}
                </div>
                <div className='flex flex-col gap-1'>
                  <FormLabel>Reason</FormLabel>
                  {REJECT_REASONS.map((r) => (
                    <RadioRow key={r.key} on={reason === r.key} onClick={() => setReason(r.key)} label={r.label} />
                  ))}
                </div>
                <label className='flex flex-col gap-1.5'>
                  <FormLabel>Description</FormLabel>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder='What needs to change?' rows={3} className={formArea} />
                </label>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='flex-1 text-[13px] text-text-secondary'>{returnHint}</span>
                  <PillButton onClick={() => setMode(null)}>Cancel</PillButton>
                  <PillButton variant='primary' disabled={!!returnHint || busy} tip={returnHint} onClick={() => act({ type: 'return', description: `${reasonOf(reason) ?? ''} — ${text.trim()}`.replace(/^ — /, '') })}>
                    Return
                  </PillButton>
                </div>
              </div>
            )}
          </div>
        </section>

        {final && (
          <div className='flex items-center gap-3 rounded-xl border border-green-deep03/[0.12] bg-surface-green10 px-4 py-3'>
            <CycleIcon className='h-5 w-5 shrink-0 text-green-deep02' />
            <div className='flex flex-col gap-0.5'>
              <span className='text-[14px] font-medium leading-[140%] text-text-primary'>Peer review started at {at(chain[0]?.deliverable?.submittedAt ?? item.submittedAt)}</span>
              <span className='text-[13px] leading-[140%] text-text-secondary'>3 working hours per member, offline time not counted</span>
            </div>
          </div>
        )}
        {showRubric && <Rubric key={item.id} item={item} act={act} busy={busy} />}
        {!desk && infoCard}

        {/* Work */}
        <section className='flex flex-col gap-3'>
          <SectionHead title='Work' sub='This version, its Task List and earlier versions' />
          {deliverable}
          {lastRejection && (
            <div className='flex flex-col gap-2.5 rounded-xl border border-[#ffdf56] bg-functional-warning01 px-4 py-3'>
              <span className='text-[14px] font-medium leading-[140%] text-functional-warning04'>Resubmission · last rejection</span>
              <div className='flex flex-col gap-0.5'>
                {reasonOf(lastRejection.reason) && <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{reasonOf(lastRejection.reason)}</span>}
                <span className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary'>
                  <Linkify text={lastRejection.description} />
                </span>
              </div>
              {lastRejection.failedItems?.length > 0 && (
                <div className='flex flex-col gap-1.5'>
                  <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>✗ in previous version</span>
                  {lastRejection.failedItems.map((f) => (
                    <span key={f} className='flex items-center gap-2 text-[14px] leading-[140%] text-text-primary'>
                      <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-functional-failed01 text-[12px] font-medium text-functional-failed04'>✗</span>
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className={cn(card, 'flex flex-col gap-4 px-6 py-5')}>
            <div className='flex items-center justify-between gap-3'>
              <span className='text-[15px] font-medium leading-[140%] text-text-primary'>{slotLabels.length ? 'Links and Task List' : 'Task List'}</span>
              <span className='text-[13px] tabular-nums text-text-secondary'>
                {yN} ✓ · {nN} ✗ · {keys - yN - nN} left
              </span>
            </div>
            {groups.map(([title, items]) => (
              <div key={title} className='flex flex-col'>
                <span className='pb-1.5 pt-1 text-[12px] font-medium leading-[140%] text-text-secondary'>{title}</span>
                {items.map(([k, label]) => (
                  <div key={k} className='flex min-h-12 items-center gap-3 border-t border-green-deep03/[0.06] py-1.5'>
                    <span className={cn('min-w-0 flex-1 text-[14px] leading-[140%]', marks[k] === 'fail' ? 'text-functional-failed04' : 'text-text-primary')}>{label}</span>
                    <div className='flex shrink-0 gap-1.5'>
                      <MarkButton kind='pass' active={marks[k] === 'pass'} disabled={!sub} onClick={() => toggle(k, 'pass')} />
                      <MarkButton kind='fail' active={marks[k] === 'fail'} disabled={!sub} onClick={() => toggle(k, 'fail')} />
                    </div>
                  </div>
                ))}
                {items.length === 0 && <span className='py-2 text-[13px] text-text-tertiary'>No items</span>}
              </div>
            ))}
          </div>
          <div className={cn(card, 'flex flex-col overflow-hidden rounded-xl')}>
            <span className='px-4 pb-1 pt-3 text-[13px] font-medium leading-[140%] text-text-secondary'>Versions</span>
            {[...chain].reverse().map((x) => {
              const r = x.review;
              const chip = r?.result === 'approved' ? ['Approved', 'success'] : r?.returned ? ['Returned', 'warning'] : r ? ['Rejected', 'failed'] : ['In review', 'accent'];
              const cur = x.version === viewing.version;
              return (
                <button key={x.version} type='button' onClick={() => setViewV(x.version === item.version ? null : x.version)} className={cn('flex flex-col gap-1 border-t border-green-deep03/[0.08] px-4 py-3 text-left hover:bg-green-deep03/[0.03]', cur && 'bg-green-deep03/[0.03]')}>
                  <span className='flex flex-wrap items-center gap-2'>
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>v{x.version}</span>
                    <Pill tone={chip[1]}>{chip[0]}</Pill>
                    {cur && <span className='text-[12px] text-text-secondary'>Viewing</span>}
                  </span>
                  <span className='text-[13px] leading-[150%] text-text-secondary'>
                    Submitted {at(x.deliverable?.submittedAt)}
                    {r?.at ? ` · ${r.result === 'approved' ? 'approved' : r.returned ? 'returned' : 'rejected'} ${at(r.at)}` : ''}
                  </span>
                  {r && r.result !== 'approved' && (r.reason || r.description) && <span className='text-[13px] leading-[150%] text-functional-failed04 [text-wrap:pretty]'>{[reasonOf(r.reason), r.description].filter(Boolean).join(' — ')}</span>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Activity */}
        <section className='flex flex-col gap-3'>
          <SectionHead title='Activity' sub='From first submission to now · newest first' right={<TabPills tabs={actTabs} value={tab} onChange={setTab} />} />
          <div className={cn(card, 'flex flex-col gap-2.5 p-4')}>
            <div className='flex flex-wrap items-center gap-2'>
              <div role='radiogroup' className='flex rounded-[10px] bg-green-deep03/[0.06] p-[3px]'>
                {[['question', 'Question'], ['general', 'General']].map(([k, label]) => (
                  <button key={k} type='button' aria-pressed={cType === k} onClick={() => setCType(k)} className={cn('h-[30px] rounded-lg px-3.5 text-[13px]', cType === k ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-4' : 'text-text-tertiary')}>
                    {label}
                  </button>
                ))}
              </div>
              {cType === 'question' && (
                <Select value={cTarget} onChange={(e) => setCTarget(e.target.value)} aria-label='Receiver' className='h-9 w-auto rounded-lg'>
                  <option value=''>Pick 1 receiver</option>
                  {members.filter((m) => m.role !== 'npc' && m.status !== 'dropped').map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}{JOB_LABEL[m.job] ? ` · ${JOB_LABEL[m.job]}` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </div>
            <textarea value={cBody} onChange={(e) => setCBody(e.target.value)} rows={3} maxLength={4000} placeholder={cType === 'question' ? 'Ask one clear question' : 'Write a comment'} className={formArea} />
            <div className='flex flex-wrap items-center gap-2'>
              <span className='flex-1 text-[12px] text-text-secondary'>Comments can’t be edited or deleted.</span>
              {cBody.trim() && cType === 'question' && !cTarget && <span className='text-[13px] text-text-secondary'>Pick a receiver</span>}
              <PillButton size='s' className='h-9 px-[18px] text-[14px]' variant='primary' disabled={!canPost || busy} tip={cType === 'question' && !cTarget ? 'Pick a receiver' : 'Write a comment'} onClick={post}>
                Post
              </PillButton>
            </div>
          </div>
          {tab === 'cmt' ? (
            <section className={cn(card, 'p-4')}>
              <WorkboardCtxOverride.Provider value={commentCtx}>
                <Comments task={{ id: item.id }} />
              </WorkboardCtxOverride.Provider>
            </section>
          ) : (
            <LogList events={F[tab]} empty='Nothing yet' />
          )}
        </section>
      </div>
    </div>
  );
};
